import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Divider,
    IconButton,
    Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const maerskFontFamily = '"Maersk Text", -apple-system, BlinkMacSystemFont, "Microsoft JhengHei", "Microsoft Yahei", "微软雅黑", STXihei, "华文细黑", sans-serif';

const AIAnalysisTab = ({ changeData, changeNumber, setisdrawerOpen, setDrawerCi, setDrawerCiCategory, setDrawerType }) => {
    const details = changeData?.change_details;
    const affectedCis = changeData?.affected_cis || [];

    // Handle CI info click - opens drawer (if drawer props are available)
    const handleCiInfoClick = (ciName, category = 'Application') => {
        if (setisdrawerOpen && setDrawerCi && setDrawerCiCategory && setDrawerType) {
            setTimeout(() => {
                setisdrawerOpen(true);
                setDrawerCi(ciName);
                setDrawerCiCategory(category);
                setDrawerType("ci_info");
            }, 0);
        }
    };

    // Transform affected CIs data for table
    const affectedCisTableData = affectedCis.map((ci, index) => ({
        id: `ci-${index}`,
        ciName: ci.ci_name || ci,
        className: ci.class_name || 'N/A',
        environment: ci.ci_environment || 'N/A'
    }));

    // Column definitions for Affected CIs table
    const affectedCisColumns = [
        { id: "ciName", label: "CI Name", sortable: true },
        { id: "className", label: "Class Name", sortable: true },
        { id: "environment", label: "Environment", sortable: true }
    ];

    // Handle download click - generates PDF document
    const handleDownloadClick = () => {
        try {
            const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 20;
        let yPosition = 30;

        // Helper function to add text with word wrapping and clean encoding
        const addWrappedText = (text, x, y, maxWidth, fontSize = 10) => {
            doc.setFontSize(fontSize);
            // Clean the text of any problematic characters
            const cleanText = text.replace(/[^\x20-\x7E\n\r\t]/g, '').replace(/\s+/g, ' ').trim();
            const lines = doc.splitTextToSize(cleanText, maxWidth);
            doc.text(lines, x, y);
            return y + (lines.length * (fontSize * 0.352777778)); // Convert pt to mm
        };

        // Header
        doc.setFontSize(16);
        doc.setFont(undefined, 'bold');
        doc.text('AI Change Analysis Report', margin, yPosition);
        yPosition += 10;

        // Change details
        doc.setFontSize(12);
        const displayChangeNumber = changeNumber || changeData?.change_details?.number || 'N/A';
        const shortDescription = (changeData?.change_details?.short_description || 'N/A').replace(/[^\x20-\x7E\n\r\t]/g, '').replace(/\s+/g, ' ').trim();
        
        doc.text(`Change Number: ${displayChangeNumber}`, margin, yPosition);
        yPosition += 8;
        doc.text(`Short Description: ${shortDescription}`, margin, yPosition);
        yPosition += 15;

        // Parse AI analysis if available
        if (details?.ai_change_analysis) {
            const sections = parseAIAnalysis(details.ai_change_analysis);

            // Add each section
            Object.entries(sections).forEach(([sectionTitle, content]) => {
                // Check if we need a new page
                if (yPosition > 250) {
                    doc.addPage();
                    yPosition = 30;
                }

                // Section title - map emojis to clean titles
                doc.setFontSize(14);
                doc.setFont(undefined, 'bold');
                
                const titleMapping = {
                    '📋 CHANGE SUMMARY:': 'CHANGE SUMMARY',
                    '⚙️ TECHNICAL ASSESSMENT:': 'TECHNICAL ASSESSMENT', 
                    '⚠️ RISK ASSESSMENT:': 'RISK ASSESSMENT',
                    '🔥 POTENTIAL FAILURE SCENARIOS:': 'POTENTIAL FAILURE SCENARIOS',
                    '✅ RECOMMENDATION:': 'RECOMMENDATION',
                    '📝 ADDITIONAL INFO REQUIRED:': 'ADDITIONAL INFO REQUIRED'
                };
                
                const cleanTitle = titleMapping[sectionTitle] || sectionTitle.replace(/^[📋⚙️⚠️🔥✅📝]\s*/, '').replace(/:$/, '');
                doc.text(cleanTitle, margin, yPosition);
                yPosition += 10;

                // Section content
                doc.setFont(undefined, 'normal');
                doc.setFontSize(10);
                
                // Handle array content (like failure scenarios)
                let processedContent = content;
                if (content.includes('[') && content.includes(']')) {
                    try {
                        const items = JSON.parse(content);
                        if (Array.isArray(items)) {
                            processedContent = items.map((item, index) => `${index + 1}. ${item}`).join('\n\n');
                        }
                    } catch (e) {
                        // Keep original content if parsing fails
                    }
                }
                
                // Clean content of any problematic characters
                processedContent = processedContent.replace(/[^\x20-\x7E\n\r\t]/g, '').replace(/\s+/g, ' ').trim();

                yPosition = addWrappedText(processedContent, margin, yPosition, pageWidth - (margin * 2));
                yPosition += 10;
            });
        } else {
            doc.setFontSize(12);
            doc.text('No AI analysis available for this change.', margin, yPosition);
            yPosition += 15;
        }

        // Add Affected CIs table if available
        if (affectedCisTableData.length > 0) {
            // Check if we need a new page
            if (yPosition > 200) {
                doc.addPage();
                yPosition = 30;
            }

            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('Affected Configuration Items', margin, yPosition);
            yPosition += 10;

            // Prepare table data
            const tableColumns = ['CI Name', 'Class Name', 'Environment'];
            const tableRows = affectedCisTableData.map(ci => [
                ci.ciName,
                ci.className,
                ci.environment
            ]);

            // Add table using autoTable
            autoTable(doc, {
                head: [tableColumns],
                body: tableRows,
                startY: yPosition,
                margin: { left: margin, right: margin },
                styles: {
                    fontSize: 9,
                    cellPadding: 3
                },
                headStyles: {
                    fillColor: [66, 139, 202],
                    textColor: 255,
                    fontStyle: 'bold'
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                }
            });
        } else {
            // Check if we need a new page
            if (yPosition > 250) {
                doc.addPage();
                yPosition = 30;
            }

            doc.setFontSize(14);
            doc.setFont(undefined, 'bold');
            doc.text('Affected Configuration Items', margin, yPosition);
            yPosition += 10;

            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text('No affected CIs found', margin, yPosition);
        }

        // Generate filename
        const fileChangeNumber = changeNumber || changeData?.change_details?.number || 'Unknown';
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `AI_Analysis_${fileChangeNumber}_${timestamp}.pdf`;

            // Save the PDF
            doc.save(filename);
        } catch (error) {
            console.error('Error generating PDF:', error);
            // You could add a toast notification here if available
            alert('Error generating PDF. Please try again.');
        }
    };
    
    if (!details?.ai_change_analysis) {
        return (
            <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px' }}>
                <CardContent sx={{ padding: '24px' }}>
                    <Typography variant="body1" sx={{ fontFamily: maerskFontFamily, color: '#666' }}>
                        No AI analysis available for this change.
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    // Parse AI analysis content into sections
    const parseAIAnalysis = (content) => {
        const sections = {};
        const lines = content.split('\n');
        let currentSection = null;
        let currentContent = [];

        lines.forEach(line => {
            if (line.includes('=== AI CHANGE MANAGER REVIEW ===') || line.includes('--- End of AI Review ---')) {
                return; // Skip header and footer
            }

            if (line.match(/^📋 CHANGE SUMMARY:|^⚙️ TECHNICAL ASSESSMENT:|^⚠️ RISK ASSESSMENT:|^🔥 POTENTIAL FAILURE SCENARIOS:|^✅ RECOMMENDATION:|^📝 ADDITIONAL INFO REQUIRED:/)) {
                // Save previous section
                if (currentSection && currentContent.length > 0) {
                    sections[currentSection] = currentContent.join('\n').trim();
                }
                // Start new section
                currentSection = line.trim();
                currentContent = [];
            } else if (currentSection && line.trim() !== '') {
                currentContent.push(line);
            }
        });

        // Save last section
        if (currentSection && currentContent.length > 0) {
            sections[currentSection] = currentContent.join('\n').trim();
        }

        return sections;
    };

    const sections = parseAIAnalysis(details.ai_change_analysis);

    return (
        <Box>
            {/* Header with Download Button */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                mb: 3,
                alignItems: 'center'
            }}>
                <McButton
                    label="Download"
                    appearance="primary"
                    variant="filled"
                    fit="small"
                    click={handleDownloadClick}
                >
                    <span slot="trailingicon">
                        <mc-icon class="my-icon" icon="tray-arrow-down" color="white"></mc-icon>
                    </span>
                </McButton>
            </Box>

            {/* Main Content */}
            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Combined Analysis Card */}
                    {(sections['📋 CHANGE SUMMARY:'] || sections['⚙️ TECHNICAL ASSESSMENT:'] || sections['⚠️ RISK ASSESSMENT:']) && (
                        <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px' }}>
                            <CardContent sx={{ padding: '24px' }}>
                                {/* Change Summary */}
                                {sections['📋 CHANGE SUMMARY:'] && (
                                    <>
                                        <Typography variant="h6" gutterBottom sx={{ fontFamily: maerskFontFamily, color: '#333' }}>
                                            Change Summary
                                        </Typography>
                                        <Typography 
                                            variant="body1" 
                                            sx={{ 
                                                fontFamily: maerskFontFamily, 
                                                lineHeight: 1.6,
                                                color: '#555',
                                                whiteSpace: 'pre-wrap',
                                                mb: 3
                                            }}
                                        >
                                            {sections['📋 CHANGE SUMMARY:']}
                                        </Typography>
                                    </>
                                )}

                                {/* Divider */}
                                {sections['📋 CHANGE SUMMARY:'] && (sections['⚙️ TECHNICAL ASSESSMENT:'] || sections['⚠️ RISK ASSESSMENT:']) && (
                                    <Divider sx={{ my: 3 }} />
                                )}

                                {/* Technical Assessment */}
                                {sections['⚙️ TECHNICAL ASSESSMENT:'] && (
                                    <Box sx={{ mb: 4 }}>
                                        <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500, mb: 1 }}>
                                            Technical Assessment
                                        </Typography>
                                        <Typography 
                                            variant="body1" 
                                            sx={{ 
                                                fontFamily: maerskFontFamily, 
                                                lineHeight: 1.6,
                                                color: '#555',
                                                whiteSpace: 'pre-wrap'
                                            }}
                                        >
                                            {sections['⚙️ TECHNICAL ASSESSMENT:']}
                                        </Typography>
                                    </Box>
                                )}

                                {/* Risk Assessment */}
                                {sections['⚠️ RISK ASSESSMENT:'] && (
                                    <Box sx={{ mb: 1 }}>
                                        <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500, mb: 1 }}>
                                            Risk Assessment
                                        </Typography>
                                        <Typography 
                                            variant="body1" 
                                            sx={{ 
                                                fontFamily: maerskFontFamily, 
                                                lineHeight: 1.6,
                                                color: '#555',
                                                whiteSpace: 'pre-wrap'
                                            }}
                                        >
                                            {sections['⚠️ RISK ASSESSMENT:']}
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </Box>

                <Box sx={{ width: { xs: '100%', md: '40%' }, flexShrink: 0 }}>
                    {/* Combined Scenarios and Recommendation Card */}
                    {(sections['🔥 POTENTIAL FAILURE SCENARIOS:'] || sections['✅ RECOMMENDATION:']) && (
                        <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px' }}>
                            <CardContent sx={{ padding: '24px' }}>
                                {/* Potential Failure Scenarios */}
                                {sections['🔥 POTENTIAL FAILURE SCENARIOS:'] && (
                                    <>
                                        <Typography variant="h6" gutterBottom sx={{ fontFamily: maerskFontFamily, color: '#333' }}>
                                            Potential Failure Scenarios
                                        </Typography>
                                        <Typography 
                                            variant="body1" 
                                            sx={{ 
                                                fontFamily: maerskFontFamily, 
                                                lineHeight: 1.6,
                                                color: '#555',
                                                whiteSpace: 'pre-wrap',
                                                mb: 3
                                            }}
                                        >
                                            {(() => {
                                                const content = sections['🔥 POTENTIAL FAILURE SCENARIOS:'];
                                                // Parse array-like content and format it nicely
                                                if (content.includes('[') && content.includes(']')) {
                                                    try {
                                                        const scenarios = JSON.parse(content);
                                                        return scenarios.map((scenario, index) => `${index + 1}. ${scenario}`).join('\n\n');
                                                    } catch (e) {
                                                        return content;
                                                    }
                                                }
                                                return content;
                                            })()}
                                        </Typography>
                                    </>
                                )}

                                {/* Divider */}
                                {sections['🔥 POTENTIAL FAILURE SCENARIOS:'] && sections['✅ RECOMMENDATION:'] && (
                                    <Divider sx={{ my: 3 }} />
                                )}

                                {/* Recommendation */}
                                {sections['✅ RECOMMENDATION:'] && (
                                    <Box sx={{ mb: 1 }}>
                                        <Typography variant="body2" color="textSecondary" sx={{ fontFamily: maerskFontFamily, fontWeight: 500, mb: 1 }}>
                                            Recommendation
                                        </Typography>
                                        <Typography 
                                            variant="body1" 
                                            sx={{ 
                                                fontFamily: maerskFontFamily, 
                                                lineHeight: 1.6,
                                                color: '#555',
                                                whiteSpace: 'pre-wrap',
                                                fontWeight: 500
                                            }}
                                        >
                                            {sections['✅ RECOMMENDATION:']}
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Additional Info Required */}
                    {sections['📝 ADDITIONAL INFO REQUIRED:'] && (
                        <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px' }}>
                            <CardContent sx={{ padding: '24px' }}>
                                <Typography variant="h6" gutterBottom sx={{ fontFamily: maerskFontFamily, color: '#333' }}>
                                    Additional Info Required
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        fontFamily: maerskFontFamily, 
                                        lineHeight: 1.6,
                                        color: '#555',
                                        whiteSpace: 'pre-wrap'
                                    }}
                                >
                                    {(() => {
                                        const content = sections['📝 ADDITIONAL INFO REQUIRED:'];
                                        // Parse array-like content and format it nicely
                                        if (content.includes('[') && content.includes(']')) {
                                            try {
                                                const items = JSON.parse(content);
                                                return items.map((item, index) => `${index + 1}. ${item}`).join('\n\n');
                                            } catch (e) {
                                                return content;
                                            }
                                        }
                                        return content;
                                    })()}
                                </Typography>
                            </CardContent>
                        </Card>
                    )}

                    {/* Affected CIs Table */}
                    <Card sx={{ mb: 3, border: '1px solid #d9d9d9', boxShadow: 'none', borderRadius: '10px' }}>
                        <CardContent sx={{ padding: '24px' }}>
                            <Typography variant="h6" gutterBottom sx={{ fontFamily: maerskFontFamily, color: '#333' }}>
                                Affected CIs
                            </Typography>
                            {affectedCisTableData.length > 0 ? (
                                <McTable
                                    data={affectedCisTableData}
                                    columns={affectedCisColumns}
                                    datakey="id"
                                    headersticky
                                    fit="small"
                                    customstyles=".mds-table { max-height: 400px !important; }"
                                >
                                    {affectedCisTableData.map((row) => (
                                        <React.Fragment key={row.id}>
                                            <div slot={`${row.id}_ciName`}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <span style={{ fontFamily: maerskFontFamily }}>
                                                        {row.ciName}
                                                    </span>
                                                    {setisdrawerOpen && row.ciName !== 'N/A' && (
                                                        <Tooltip title="Show CI Info">
                                                            <IconButton
                                                                style={{
                                                                    marginLeft: '0.2rem',
                                                                    padding: 0,
                                                                    verticalAlign: 'top'
                                                                }}
                                                                tabIndex={-1}
                                                                onClick={() => handleCiInfoClick(row.ciName, 'Application')}
                                                                aria-label="Show CI info"
                                                            >
                                                                <InfoOutlinedIcon fontSize="small" style={{ fontWeight: "normal" }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </div>
                                            <div slot={`${row.id}_className`}>
                                                <span style={{ fontFamily: maerskFontFamily }}>
                                                    {row.className}
                                                </span>
                                            </div>
                                            <div slot={`${row.id}_environment`}>
                                                <span style={{ fontFamily: maerskFontFamily }}>
                                                    {row.environment}
                                                </span>
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </McTable>
                            ) : (
                                <Typography variant="body2" sx={{ fontFamily: maerskFontFamily, color: '#666' }}>
                                    No affected CIs found
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Box>
    );
};

export default AIAnalysisTab;
